import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import logoBg from './assets/orizon-logo.png';
import letterO from './assets/letter-o.png';
import { Link } from 'react-router-dom';
import CookieBanner from "./components/CookieBanner";

import './App.css';

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInAnonymously,
  onAuthStateChanged,
  signOut
} from 'firebase/auth';

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

import { auth, db, googleProvider } from './firebase';

const HomePage = lazy(() => import('./components/pages/HomePage'));
const FormulePage = lazy(() => import('./components/pages/FormulePage'));
const CalcoloPuntiPage = lazy(() => import('./components/pages/CalcoloPuntiPage'));
const FresaturaPage = lazy(() => import('./components/pages/FresaturaPage'));
const ForaturaPage = lazy(() => import('./components/pages/ForaturaPage'));
const EditorPage = lazy(() => import('./components/pages/EditorPage'));
const AIAssistantPage = lazy(() => import('./components/pages/AIAssistantPage'));
const CookiePage = lazy(() => import('./components/legal/CookiePage'));
const PrivacyPage = lazy(() => import('./components/legal/PrivacyPage'));
const TerminiPage = lazy(() => import('./components/legal/TerminiPage'));
const Registrazione = lazy(() => import('./components/auth/Registrazione'));

const UI_STRINGS = {
  en: {
    appTitleSuffix: 'rizon CNC Generator',
    nav_home: 'Home',
    nav_formule: 'Formulas',
    nav_points: 'Points calculator',
    nav_milling: 'Milling',
    nav_drilling: 'Drilling',
    nav_editor: 'Editor',
    nav_ai: 'AI Assistant',
    authRequiredTitle: 'Access required',
    authRequiredText:
      'You can use 3 demo calculations without an account. To continue using all features, register and unlock 30 free trial days.',
    guestDemoButton: 'Try 3 free calculation demos',
    authOpenButton: 'Register / Login',
    loadingAccess: 'Checking access...',
    loadingProfile: 'Loading profile...',
    loadingProfileText: 'Checking access and profile.',
    profileErrorTitle: 'Firestore profile error',
    profileErrorHelp: 'Check Firestore Database, Rules and Firebase project.',
    profileMissingTitle: 'Profile not available',
    profileMissingText: 'The user document in Firestore was not found.',
    expiredTitle: 'Access expired',
    expiredText: 'Your free trial or subscription has ended.',
    expiredInfo:
      'To continue using Orizon CNC Generator, complete the payment with PayPal. After payment confirmation, your access will be reactivated manually.',
    monthlyPlan: 'Monthly subscription - 4,99 €',
    annualPlan: 'Annual subscription - 49,99 €',
    logout: 'Logout',
    closeAuth: 'Close authentication window',
    languageLabel: 'Select language'
  },

  it: {
    appTitleSuffix: 'rizon CNC Generator',
    nav_home: 'Pagina iniziale',
    nav_formule: 'Formule',
    nav_points: 'Calcolo punti',
    nav_milling: 'Fresatura',
    nav_drilling: 'Foratura',
    nav_editor: 'Editor',
    nav_ai: 'AI Assistant',
    authRequiredTitle: 'Accesso richiesto',
    authRequiredText:
      'Puoi usare 3 calcoli demo senza account. Per continuare ad usare tutte le funzioni, registrati e sblocca 30 giorni di prova gratuita.',
    guestDemoButton: 'Prova 3 calcoli demo gratis',
    authOpenButton: 'Registrati / Accedi',
    loadingAccess: 'Caricamento...',
    loadingProfile: 'Caricamento profilo...',
    loadingProfileText: 'Verifica accesso e profilo in corso.',
    profileErrorTitle: 'Errore profilo Firestore',
    profileErrorHelp: 'Controlla Firestore Database, Rules e progetto Firebase.',
    profileMissingTitle: 'Profilo non disponibile',
    profileMissingText: 'Il documento utente in Firestore non è stato trovato.',
    expiredTitle: 'Accesso scaduto',
    expiredText: 'Il tuo periodo di prova o abbonamento è terminato.',
    expiredInfo:
      'Per continuare ad usare Orizon CNC Generator, effettua il pagamento con PayPal. Dopo la conferma del pagamento, il tuo accesso verrà riattivato manualmente.',
    monthlyPlan: 'Abbonamento mensile - 4,99 €',
    annualPlan: 'Abbonamento annuale - 49,99 €',
    logout: 'Logout',
    closeAuth: 'Chiudi finestra autenticazione',
    languageLabel: 'Seleziona lingua'
  },

  bs: {
    appTitleSuffix: 'rizon CNC Generator',
    nav_home: 'Početna stranica',
    nav_formule: 'Formule',
    nav_points: 'Proračun tačaka',
    nav_milling: 'Glodanje',
    nav_drilling: 'Bušenje',
    nav_editor: 'Editor',
    nav_ai: 'AI Asistent',
    authRequiredTitle: 'Pristup je potreban',
    authRequiredText:
      'Možete koristiti 3 demo proračuna bez naloga. Za nastavak korištenja svih funkcija registrujte se i otključajte 30 dana besplatnog probnog perioda.',
    guestDemoButton: 'Probaj 3 demo proračuna besplatno',
    authOpenButton: 'Registracija / Prijava',
    loadingAccess: 'Učitavanje...',
    loadingProfile: 'Učitavanje profila...',
    loadingProfileText: 'Provjera pristupa i profila je u toku.',
    profileErrorTitle: 'Greška Firestore profila',
    profileErrorHelp: 'Provjerite Firestore Database, Rules i Firebase projekat.',
    profileMissingTitle: 'Profil nije dostupan',
    profileMissingText: 'Korisnički dokument u Firestore nije pronađen.',
    expiredTitle: 'Pristup je istekao',
    expiredText: 'Vaš probni period ili pretplata su završeni.',
    expiredInfo:
      'Za nastavak korištenja Orizon CNC Generator-a izvršite uplatu putem PayPal-a. Nakon potvrde uplate, vaš pristup će biti ručno ponovo aktiviran.',
    monthlyPlan: 'Mjesečna pretplata - 4,99 €',
    annualPlan: 'Godišnja pretplata - 49,99 €',
    logout: 'Odjava',
    closeAuth: 'Zatvori prozor za autentifikaciju',
    languageLabel: 'Odaberite jezik'
  },

  de: {
    appTitleSuffix: 'rizon CNC Generator',
    nav_home: 'Startseite',
    nav_formule: 'Formeln',
    nav_points: 'Punktberechnung',
    nav_milling: 'Fräsen',
    nav_drilling: 'Bohren',
    nav_editor: 'Editor',
    nav_ai: 'AI-Assistent',
    authRequiredTitle: 'Zugriff erforderlich',
    authRequiredText:
      'Sie können 3 Demo-Berechnungen ohne Konto verwenden. Um alle Funktionen weiter zu nutzen, registrieren Sie sich und schalten Sie 30 kostenlose Testtage frei.',
    guestDemoButton: '3 kostenlose Demo-Berechnungen starten',
    authOpenButton: 'Registrieren / Anmelden',
    loadingAccess: 'Laden...',
    loadingProfile: 'Profil wird geladen...',
    loadingProfileText: 'Zugriff und Profil werden überprüft.',
    profileErrorTitle: 'Firestore-Profilfehler',
    profileErrorHelp: 'Prüfen Sie Firestore Database, Rules und das Firebase-Projekt.',
    profileMissingTitle: 'Profil nicht verfügbar',
    profileMissingText: 'Das Benutzerdokument in Firestore wurde nicht gefunden.',
    expiredTitle: 'Zugriff abgelaufen',
    expiredText: 'Ihre Testphase oder Ihr Abonnement ist beendet.',
    expiredInfo:
      'Um Orizon CNC Generator weiter zu nutzen, führen Sie die Zahlung mit PayPal durch. Nach Bestätigung der Zahlung wird Ihr Zugriff manuell wieder aktiviert.',
    monthlyPlan: 'Monatsabo - 4,99 €',
    annualPlan: 'Jahresabo - 49,99 €',
    logout: 'Abmelden',
    closeAuth: 'Authentifizierungsfenster schließen',
    languageLabel: 'Sprache auswählen'
  },
};

const MAX_GUEST_DEMOS = 3;
const API_BASE = 'https://api-yebemxvz3a-uc.a.run.app';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [type, setType] = useState('');
  const [params, setParams] = useState({});
  const [gcode, setGcode] = useState('');
  const [punti, setPunti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editorText, setEditorText] = useState('');
  const [language, setLanguage] = useState('it');

  const [aiMessages, setAiMessages] = useState([]);
  const [aiExplanation, setAiExplanation] = useState('');
  const [aiGcode, setAiGcode] = useState('');

  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [guestProfile, setGuestProfile] = useState(null);

  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirmPassword, setAuthConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authActionLoading, setAuthActionLoading] = useState(false);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [demoCompletedOnDevice, setDemoCompletedOnDevice] = useState(false);

  const labels = UI_STRINGS[language] || UI_STRINGS.it;
  const authLabels = labels.auth || {
    subtitle: 'Registrati o accedi per usare la piattaforma. Hai 30 giorni di prova gratis.',
    login: 'Login',
    register: 'Registrazione',
    emailPlaceholder: 'Inserisci la tua email',
    passwordPlaceholder: 'Inserisci la password',
    confirmPasswordPlaceholder: 'Ripeti la password',
    loginButton: 'Accedi',
    registerButton: 'Crea account',
    googleButton: 'Accedi con Google',
    loading: 'Attendere...'
  };

  const isAnonymousUser = !!currentUser?.isAnonymous;
  const isRegisteredUser = !!currentUser && !currentUser.isAnonymous;
  const demoUsedCount = isAnonymousUser ? (userProfile?.demoUsedCount || 0) : 0;

  useEffect(() => {
    document.body.classList.add('app-hydrated');
    return () => {
      document.body.classList.remove('app-hydrated');
    };
  }, []);

  const createAnonymousUserProfile = async (user) => {
    const guestData = {
      email: '',
      userId: user.uid,
      accountType: 'guest',
      demoUsedCount: 0,
      demoLimit: MAX_GUEST_DEMOS,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'users', user.uid), guestData, { merge: true });
    return guestData;
  };

  const createInitialUserProfile = async (user) => {
    const now = new Date();
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 30);

    const profileData = {
      email: user.email || '',
      userId: user.uid,
      createdAt: serverTimestamp(),
      trialStart: Timestamp.fromDate(now),
      trialEndsAt: Timestamp.fromDate(trialEnd),
      accessEndsAt: Timestamp.fromDate(trialEnd),
      subscriptionPlan: 'trial',
      subscriptionStatus: 'active',
      paypalSubscriptionId: '',
      adminNotes: '',
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'users', user.uid), profileData, { merge: true });
    return profileData;
  };

  const withTimeout = (promise, ms = 10000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout Firestore')), ms)
      ),
    ]);
  };

  const loadUserProfile = async (user) => {
    try {
      setProfileLoading(true);
      setProfileError('');

      const userRef = doc(db, 'users', user.uid);
      let snap = await withTimeout(getDoc(userRef), 10000);

      if (!snap.exists()) {
        if (user.isAnonymous) {
          await withTimeout(createAnonymousUserProfile(user), 10000);
        } else {
          await withTimeout(createInitialUserProfile(user), 10000);
        }

        snap = await withTimeout(getDoc(userRef), 10000);
      }

      if (snap.exists()) {
        const profileData = { id: snap.id, ...snap.data() };
        setUserProfile(profileData);

        if (user.isAnonymous) {
          setGuestProfile(profileData);
        } else {
          setGuestProfile(null);
        }
      } else {
        setUserProfile(null);
        setGuestProfile(null);
        setProfileError('Profilo Firestore non trovato o non creato.');
      }
    } catch (error) {
      console.error('Errore loadUserProfile:', error);
      setUserProfile(null);
      setGuestProfile(null);
      setProfileError(error.message || 'Errore durante caricamento profilo Firestore.');
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    fetch(`${API_BASE}/lavorazioni`)
      .then(res => res.json())
      .then(data => console.log('Lavorazioni caricate:', data))
      .catch(err => console.log('Backend non pronto:', err));
  }, []);

  useEffect(() => {
    const flag = window.localStorage.getItem('orizon_demo_completed');
    if (flag === 'true') {
      setDemoCompletedOnDevice(true);
    }
  }, []);

  useEffect(() => {
    const video = document.getElementById('cnc-bg-video');
    if (video) {
      video.playbackRate = 0.5;
    }
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthLoading(true);

      if (user) {
        setCurrentUser(user);
        setShowAuthModal(false);
        await loadUserProfile(user);
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setGuestProfile(null);
        setProfileError('');
        setProfileLoading(false);
      }

      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleParamChange = (name, value) => {
    setParams(prev => ({ ...prev, [name]: value }));
  };

  const resetGeneratorState = () => {
    setType('');
    setParams({});
    setGcode('');
    setPunti([]);
    setShowPreview(false);
  };

  const goToRoute = (path) => {
    resetGeneratorState();
    navigate(path);
  };

  const handleGenerate = async () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (currentUser.isAnonymous && demoUsedCount >= MAX_GUEST_DEMOS) {
      window.localStorage.setItem('orizon_demo_completed', 'true');
      setDemoCompletedOnDevice(true);
      setShowAuthModal(true);
      return;
    }

    if (!type) {
      alert('Seleziona una lavorazione');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, params }),
      });

      const data = await res.json();

      if (data.gcode) {
        setGcode(data.gcode);

        if (data.punti) {
          setPunti(data.punti);
        }

        setShowPreview(true);

        if (currentUser.isAnonymous) {
          const newCount = demoUsedCount + 1;

          await setDoc(
            doc(db, 'users', currentUser.uid),
            {
              demoUsedCount: newCount,
              demoLimit: MAX_GUEST_DEMOS,
              updatedAt: serverTimestamp()
            },
            { merge: true }
          );

          setUserProfile((prev) => ({
            ...prev,
            demoUsedCount: newCount
          }));

          setGuestProfile((prev) => ({
            ...prev,
            demoUsedCount: newCount
          }));

          if (newCount >= MAX_GUEST_DEMOS) {
            window.localStorage.setItem('orizon_demo_completed', 'true');
            setDemoCompletedOnDevice(true);
          }
        }
      } else if (data.error) {
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
      alert('Errore di connessione al server remoto. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewFromEditor = () => {
    if (!editorText.trim()) {
      alert('Nessun codice da visualizzare.');
      return;
    }

    setGcode(editorText);
    setPunti([]);
    setShowPreview(true);
    navigate('/fresatura');
  };

  const handleFromAIAssistantToEditor = (code) => {
    setEditorText(code || '');
    navigate('/editor');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');

    if (authPassword !== authConfirmPassword) {
      setAuthError('Le password non coincidono.');
      return;
    }

    try {
      setAuthActionLoading(true);
      const cred = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
      await loadUserProfile(cred.user);
      setShowAuthModal(false);
    } catch (error) {
      console.error(error);
      setAuthError(error.message || 'Errore durante la registrazione.');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');

    try {
      setAuthActionLoading(true);
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
      setShowAuthModal(false);
    } catch (error) {
      console.error(error);
      setAuthError('Email o password non validi.');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError('');

    try {
      setAuthActionLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      await loadUserProfile(result.user);
      setShowAuthModal(false);
    } catch (error) {
      console.error(error);
      setAuthError('Errore durante accesso con Google.');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (currentUser?.isAnonymous) {
        navigate('/');
        setShowAuthModal(false);
        return;
      }

      await signOut(auth);
      navigate('/');
      setShowAuthModal(false);
    } catch (error) {
      console.error('Errore logout:', error);
    }
  };

  const openAuthModal = () => {
    setAuthError('');
    setAuthMode('login');
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
    setAuthError('');
  };

  const handleStartGuestDemo = async () => {
    try {
      if (demoCompletedOnDevice) {
        setShowAuthModal(true);
        return;
      }

      if (!currentUser) {
        const cred = await signInAnonymously(auth);
        await loadUserProfile(cred.user);
      }

      const effectiveUser = currentUser || auth.currentUser;
      const effectiveDemoCount =
        effectiveUser?.isAnonymous
          ? (userProfile?.demoUsedCount || guestProfile?.demoUsedCount || 0)
          : 0;

      if (effectiveUser?.isAnonymous && effectiveDemoCount >= MAX_GUEST_DEMOS) {
        window.localStorage.setItem('orizon_demo_completed', 'true');
        setDemoCompletedOnDevice(true);
        setShowAuthModal(true);
        return;
      }

      navigate('/calcolo-punti');
    } catch (error) {
      console.error('Errore avvio demo anonima:', error);
      alert('Impossibile avviare la demo in questo momento. Riprova.');
    }
  };

  const accessEndsAt = !isAnonymousUser ? userProfile?.accessEndsAt?.toDate?.() : null;
  const hasAccess =
    accessEndsAt instanceof Date && accessEndsAt.getTime() > Date.now();

  const canUseGenerator = currentUser
    ? (isAnonymousUser ? demoUsedCount < MAX_GUEST_DEMOS : hasAccess)
    : false;

  const requiresAccessPaths = [
    '/calcolo-punti',
    '/fresatura',
    '/foratura',
    '/editor',
    '/ai-assistant'
  ];

  if (authLoading) {
    return (
      <div className="App">
        <video id="cnc-bg-video" className="page-bg-video" autoPlay muted loop playsInline>
          
        </video>
        <div className="auth-page">
          <div className="auth-card">
            <h2 className="auth-title">{labels.loadingAccess}</h2>
            <p className="auth-subtitle">Verifica accesso in corso.</p>
          </div>
        </div>
      </div>
    );
  }

  if (currentUser && profileLoading) {
    return (
      <div className="App">
        <video id="cnc-bg-video" className="page-bg-video" autoPlay muted loop playsInline>
         
        </video>
        <div className="auth-page">
          <div className="auth-card">
            <h2 className="auth-title">{labels.loadingProfile}</h2>
            <p className="auth-subtitle">{labels.loadingProfileText}</p>
          </div>
        </div>
      </div>
    );
  }

  if (currentUser && profileError) {
    return (
      <div className="App">
       
        <div className="auth-page">
          <div className="auth-card">
            <h2 className="auth-title">{labels.profileErrorTitle}</h2>
            <p className="auth-subtitle">{profileError}</p>
            <p className="auth-subtitle">{labels.profileErrorHelp}</p>
            <button type="button" className="logout-btn" onClick={handleLogout}>
              {labels.logout}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentUser && !userProfile) {
    return (
      <div className="App">
        
        <div className="auth-page">
          <div className="auth-card">
            <h2 className="auth-title">{labels.profileMissingTitle}</h2>
            <p className="auth-subtitle">{labels.profileMissingText}</p>
            <button type="button" className="logout-btn" onClick={handleLogout}>
              {labels.logout}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentUser && !currentUser.isAnonymous && userProfile && !hasAccess) {
    return (
      <div className="App">
        

        <div className="auth-page">
          <div className="auth-card">
            <h1 className="auth-title">{labels.expiredTitle}</h1>
            <p className="auth-subtitle">{labels.expiredText}</p>

            <div className="subscription-box">
              <p>{labels.expiredInfo}</p>

              <div className="subscription-actions">
                <button
                  type="button"
                  className="auth-submit"
                  onClick={() =>
                    window.open('https://paypal.me/BelminKovacevic/4.99EUR', '_blank', 'noopener,noreferrer')
                  }
                >
                  {labels.monthlyPlan}
                </button>

                <button
                  type="button"
                  className="auth-google-btn"
                  onClick={() =>
                    window.open('https://paypal.me/BelminKovacevic/49.99EUR', '_blank', 'noopener,noreferrer')
                  }
                >
                  {labels.annualPlan}
                </button>
              </div>
            </div>

            <button type="button" className="logout-btn" onClick={handleLogout}>
              {labels.logout}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderProtectedFallback = () => (
    <main className="home-main">
      <section className="home-section home-hero">
        <span className="home-badge">{labels.authRequiredTitle}</span>
        <h2 className="home-hero-title">{labels.authRequiredTitle}</h2>
        <p className="home-description home-hero-description">
          {labels.authRequiredText}
        </p>

        <div className="home-hero-actions">
          <button
            type="button"
            className="hero-primary-btn"
            onClick={handleStartGuestDemo}
          >
            {labels.guestDemoButton}
          </button>

          <button
            type="button"
            className="hero-secondary-btn"
            onClick={openAuthModal}
          >
            {labels.authOpenButton}
          </button>
        </div>
      </section>
    </main>
  );

  return (
    <div className="App">
      <header
        className="app-header"
        style={{
          '--header-logo': `url(${logoBg})`,
          '--letter-o': `url(${letterO})`,
        }}
      >
        <div className="header-overlay">
          <h1 className="app-title">
            <span className="app-title-o" />
            <span className="app-title-text">{labels.appTitleSuffix}</span>
          </h1>

          <div className="language-switcher">
            <span className="language-label">{labels.languageLabel}</span>
            <div className="language-flags">
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={language === 'en' ? 'lang-btn active' : 'lang-btn'}
                title="English"
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLanguage('it')}
                className={language === 'it' ? 'lang-btn active' : 'lang-btn'}
                title="Italiano"
              >
                Italiano
              </button>
              <button
                type="button"
                onClick={() => setLanguage('bs')}
                className={language === 'bs' ? 'lang-btn active' : 'lang-btn'}
                title="Bosanski"
              >
                Bosanski
              </button>
              <button
                type="button"
                onClick={() => setLanguage('de')}
                className={language === 'de' ? 'lang-btn active' : 'lang-btn'}
                title="Deutsch"
              >
                Deutsch
              </button>
            </div>
          </div>
        </div>
      </header>

    

      <nav className="main-nav">
        <button
          className={location.pathname === '/' ? 'nav-item active' : 'nav-item'}
          onClick={() => goToRoute('/')}
        >
          {labels.nav_home}
        </button>
        <button
          className={location.pathname === '/formule' ? 'nav-item active' : 'nav-item'}
          onClick={() => goToRoute('/formule')}
        >
          {labels.nav_formule}
        </button>
        <button
          className={location.pathname === '/calcolo-punti' ? 'nav-item active' : 'nav-item'}
          onClick={() => goToRoute('/calcolo-punti')}
        >
          {labels.nav_points}
        </button>
        <button
          className={location.pathname === '/fresatura' ? 'nav-item active' : 'nav-item'}
          onClick={() => goToRoute('/fresatura')}
        >
          {labels.nav_milling}
        </button>
        <button
          className={location.pathname === '/foratura' ? 'nav-item active' : 'nav-item'}
          onClick={() => goToRoute('/foratura')}
        >
          {labels.nav_drilling}
        </button>
        <button
          className={location.pathname === '/editor' ? 'nav-item active' : 'nav-item'}
          onClick={() => goToRoute('/editor')}
        >
          {labels.nav_editor}
        </button>
        <button
          className={location.pathname === '/ai-assistant' ? 'nav-item active' : 'nav-item'}
          onClick={() => goToRoute('/ai-assistant')}
        >
          {labels.nav_ai}
        </button>

        {isRegisteredUser ? (
          <button className="nav-item nav-auth" onClick={handleLogout}>
            {labels.logout}
          </button>
        ) : (
          <button className="nav-item nav-auth" onClick={openAuthModal}>
            {labels.authOpenButton}
          </button>
        )}
      </nav>

      <Suspense
        fallback={
          <main className="home-main">
            <section className="home-section home-hero">
              <span className="home-badge">Caricamento</span>
              <h2 className="home-hero-title">Caricamento interfaccia</h2>
              <p className="home-description home-hero-description">
                Stiamo preparando i moduli di Orizon CNC Generator.
              </p>
            </section>
          </main>
        }
      >
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                language={language}
                onOpenAuth={openAuthModal}
                onStartGuestDemo={handleStartGuestDemo}
                currentUser={isRegisteredUser ? currentUser : null}
                guestCalculationCount={demoUsedCount}
                maxGuestDemos={MAX_GUEST_DEMOS}
              />
            }
          />

          <Route path="/formule" element={<FormulePage language={language} />} />

          <Route
            path="/privacy"
            element={<PrivacyPage language={language} onBack={() => navigate('/')} />}
          />

          <Route
            path="/termini"
            element={<TerminiPage language={language} onBack={() => navigate('/')} />}
          />

          <Route
            path="/cookie"
            element={<CookiePage language={language} onBack={() => navigate('/')} />}
          />

          <Route
            path="/calcolo-punti"
            element={
              canUseGenerator ? (
                <CalcoloPuntiPage language={language} />
              ) : (
                renderProtectedFallback()
              )
            }
          />

          <Route
            path="/fresatura"
            element={
              canUseGenerator ? (
                <FresaturaPage
                  language={language}
                  type={type}
                  params={params}
                  gcode={gcode}
                  punti={punti}
                  loading={loading}
                  showPreview={showPreview}
                  onChangeType={setType}
                  onParamChange={handleParamChange}
                  onGenerate={handleGenerate}
                  setShowPreview={setShowPreview}
                />
              ) : (
                renderProtectedFallback()
              )
            }
          />

          <Route
            path="/foratura"
            element={
              canUseGenerator ? (
                <ForaturaPage
                  language={language}
                  type={type}
                  params={params}
                  gcode={gcode}
                  punti={punti}
                  loading={loading}
                  showPreview={showPreview}
                  onChangeType={setType}
                  onParamChange={handleParamChange}
                  onGenerate={handleGenerate}
                  setShowPreview={setShowPreview}
                />
              ) : (
                renderProtectedFallback()
              )
            }
          />

          <Route
            path="/editor"
            element={
              canUseGenerator ? (
                <EditorPage
                  language={language}
                  value={editorText}
                  onChange={setEditorText}
                  onPreview={handlePreviewFromEditor}
                />
              ) : (
                renderProtectedFallback()
              )
            }
          />

          <Route
            path="/ai-assistant"
            element={
              canUseGenerator ? (
                <AIAssistantPage
                  language={language}
                  onSendToEditor={handleFromAIAssistantToEditor}
                  messages={aiMessages}
                  setMessages={setAiMessages}
                  explanation={aiExplanation}
                  setExplanation={setAiExplanation}
                  gcode={aiGcode}
                  setGcode={setAiGcode}
                />
              ) : (
                renderProtectedFallback()
              )
            }
          />

          <Route
            path="*"
            element={
              <HomePage
                language={language}
                onOpenAuth={openAuthModal}
                onStartGuestDemo={handleStartGuestDemo}
                currentUser={isRegisteredUser ? currentUser : null}
                guestCalculationCount={demoUsedCount}
                maxGuestDemos={MAX_GUEST_DEMOS}
              />
            }
          />
        </Routes>
      </Suspense>

      <Suspense fallback={null}>
        {!isRegisteredUser && (
          <Registrazione
            isOpen={showAuthModal}
            onClose={closeAuthModal}
            authMode={authMode}
            setAuthMode={setAuthMode}
            authEmail={authEmail}
            setAuthEmail={setAuthEmail}
            authPassword={authPassword}
            setAuthPassword={setAuthPassword}
            authConfirmPassword={authConfirmPassword}
            setAuthConfirmPassword={setAuthConfirmPassword}
            authError={authError}
            authActionLoading={authActionLoading}
            handleLogin={handleLogin}
            handleRegister={handleRegister}
            handleGoogleLogin={handleGoogleLogin}
            authLabels={authLabels}
            title="Orizon CNC Generator"
            closeLabel={labels.closeAuth}
          />
        )}
      </Suspense>

      <footer className="app-footer">
  <div className="footer-links">
    <Link
      to="/privacy"
      className="footer-btn"
      onClick={() => window.scrollTo(0, 0)}
    >
      Privacy
    </Link>

    <Link
      to="/termini"
      className="footer-btn"
      onClick={() => window.scrollTo(0, 0)}
    >
      Termini
    </Link>

    <Link
      to="/cookie"
      className="footer-btn"
      onClick={() => window.scrollTo(0, 0)}
    >
      Cookie
    </Link>
  </div>

  <a
    href="https://orizon-aix.com/"
    target="_blank"
    rel="noopener noreferrer"
  >
    Powered by Orizon-AIX
  </a>
</footer>
<CookieBanner language={language} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;